import Loader from "../../loader";
import { useEffect, useMemo, useState } from "react";
import { curSym, getProfile, getToken } from "../../../utils/pip";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOpenTalentSubscriptions,
  purchaseSubscription,
} from "../../../redux/features/find-a-job/actions/authAction";
import {
  setIsFindAJobModal,
  setRegisterStep,
} from "../../../redux/features/find-a-job/reducers/authReducer";
import { findAJobPaths } from "../../../routes/find-a-job";
import { coachPaths } from "../../../routes/coach";

const Subscription = ({ isSubscription, setIsSubscription }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedDuration, setSelectedDuration] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [planType, setPlanType] = useState("NuGlow");

  const { subLoading, subscriptionList } = useSelector(
    (state) => state.freelancer.auth
  );

  useEffect(() => {
    if (!isSubscription) return;
    setSelectedDuration("");
    const token = getToken("freelancer");
    const profile = getProfile("freelancer") || {};
    const isOpenTalent =
      profile?.role === "OPEN_TALENT" ||
      !!profile?.open_talent ||
      profile?.open_talent?.talent_type === "FREELANCER";

    if (!token || !isOpenTalent) {
      dispatch(setIsFindAJobModal(false));
      dispatch(setRegisterStep(1));
      setIsSubscription();
      return;
    }

    dispatch(fetchOpenTalentSubscriptions()).then((res) => {
      const payload = res?.payload;
      const ok = payload?.success ?? payload?.status;
      if (!payload || ok === false) {
        setFetchError("Unable to load subscription plans. Please try again.");
      } else {
        setFetchError("");
      }
      const typeFromApi =
        payload?.data?.experience?.type || payload?.data?.experience?.label;
      if (typeFromApi) {
        setPlanType(typeFromApi);
      }
    });
    setIsSubscription();
  }, [dispatch, isSubscription, setIsSubscription]);

  const durationOptions = [
    { value: 30, label: "1 - month" },
    { value: 90, label: "3 - months" },
    { value: 180, label: "6 - months" },
    { value: 365, label: "12 - months" },
  ];

  const getDurationLabel = (days) => {
    const match = durationOptions.find((option) => option.value === Number(days));
    return match ? match.label : `${days} days`;
  };

  const getPriceDurationLabel = (days) => {
    const value = Number(days);
    if (value === 365) return "year";
    if (value === 30) return "month";
    return getDurationLabel(value);
  };

  const activeDuration =
    selectedDuration === "" ? durationOptions[0]?.value : Number(selectedDuration);
  const hasExplicitDurationSelection = selectedDuration !== "";

  const filteredSubscriptions = useMemo(() => {
    const plansForDuration = (subscriptionList || []).filter(
      (item) => Number(item?.subscription_days) === activeDuration
    );

    if (!hasExplicitDurationSelection) {
      return plansForDuration;
    }

    return plansForDuration.filter((item) => Number(item?.price ?? 0) !== 0);
  }, [activeDuration, hasExplicitDurationSelection, subscriptionList]);

  const durationClass = `ct_subscription_month_${activeDuration}`;

  const formatPriceLabel = (item) => {
    const price = Number(item?.price ?? 0);
    if (price === 0) return "Free";
    if (Number(activeDuration) === 30) {
      return `${item?.dynamic_price}`;
    }
    return `${item?.dynamic_price}`;
  };

  const formatTitle = (title) => {
    const normalized = String(title || "").trim().toLowerCase();
    if (normalized === "try me" || normalized === "try-me") {
      return "Try-Me";
    }
    return title;
  };

  const getBadgeClass = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "most popular" || normalized === "best value") {
      return "ct_subscription_badge ct_subscription_badge_best ct_subscription_badge_teal";
    }
    return "ct_subscription_badge ct_subscription_badge_best";
  };


  const handlePurchasePlan = (value) => {
    const callback = (res) => {
      if (res?.success) {
        dispatch(setIsFindAJobModal(false));
        dispatch(setRegisterStep(1));
        navigate(findAJobPaths.Dashboard);
      }
    };
    const data = {
      subscription_id: value?.subscription_id,
      total_price: value?.price,
    };

    dispatch(purchaseSubscription({ payload: data, callback }));
  };

  const getPlanButtonClass = (item) => {
    const price = Number(item?.price ?? 0);
    const title = String(item?.title || "").trim().toLowerCase();
    if (price === 0) {
      return "ct_yellow_btn ct_darksky_btn  ct_mx_229";
    }
    if (["creator pro", "leader prime", "builder elite"].includes(title)) {
      return "ct_yellow_btn ct_orange_btn ct_mx_229";
    }
    return "ct_yellow_btn ct_mx_229";
  };

  const getPlanBorderColor = (item) => {
    const price = Number(item?.price ?? 0);
    const title = String(item?.title || "").trim().toLowerCase();

    if (price === 0) {
      return "#8ecae6";
    }

    if (["creator pro", "leader prime", "builder elite"].includes(title)) {
      return "#fb8500";
    }

    return "#ffb703";
  };

  const handleRedirectToPlanPage = (value) => {
    dispatch(setIsFindAJobModal(false));
    dispatch(setRegisterStep(1));
    if (value === "NuForge") {
      navigate(coachPaths?.NuForgeSubscription);
    } else if (value === "NuStar") {
      navigate(coachPaths?.NuStarSubscription);
    } else if (value == "NuGlow") {
      navigate(coachPaths?.NuGlowSubscription);
    };
  };

  if (subLoading) {
    return <Loader />;
  }
  return (
    <div>
      <section class={`py-3 ct_subscription_modal ${durationClass}`}>
        <div class="container-fluid">
          <div class="row justify-content-center">
            <div class="col-md-5 mx-auto">
              <div class="ct_subscription_logo mb-3">
                <img src="assets/img/Nuvio_logo.png" alt="Nuvio" />
              </div>
              <div class="mt-0 text-center">
                <h2 class=" ct_fw_500 mb-3 ct_darkblue_text" style={{ fontSize: "27px" }}>
                  Choose Your{" "}
                  <span className="ct_fw_600 ct_subscription_title_accent">
                    {planType || ""}
                  </span>{" "}
                  Plan
                </h2>
                <div className="d-flex justify-content-center align-items-center gap-2 mt-3 w-100 ct_subscription_select_row">
                  <div className="d-flex flex-column gap-2 position-relative w-100 ct_subscription_select_wrap">
                    <select
                      className="form-control ct_input w-100 ct_border_radius_20 ct_subscription_select"
                      value={selectedDuration}
                      onChange={(event) => setSelectedDuration(event.target.value)}
                    >
                      <option value="">Select here</option>
                      {durationOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="container-fluid ct_mt_40">
          {fetchError && (
            <div className="text-center ct_fs_16 ct_orange_text mb-4">
              {fetchError}
            </div>
          )}
          <div class="row justify-content-center">
            {filteredSubscriptions?.length != 0 &&
              filteredSubscriptions?.map((item) => (
                <div
                  key={item?.subscription_id || `${item?.title}-${item?.subscription_days}`}
                  class={`col-lg-${filteredSubscriptions?.length === 3 ? '4' : '5'} col-md-6 mb-5`}
                >
                  <div
                    class={`ct_pricing_card p-3 ct_subscription_pricing_card d-flex flex-column ${Number(item?.price ?? 0) === 0 ? "ct_subscription_free_card" : ""
                      }`}
                    style={{
                      position: "relative",
                      borderColor: getPlanBorderColor(item),
                    }}
                  >
                    <div>
                      {item?.save_percent &&
                        <span className={getBadgeClass(item?.save_percent)}>
                          {item?.save_percent}
                        </span>
                      }
                      <div class="ct_pricing_title mt-3 text-center">
                        <p class="mb-1 ct_fs_24_new text-center d-flex align-items-center gap-2 justify-content-center">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_2974_3708)">
                              <path d="M8.82554 -0.0263672C10.1693 -0.0243939 11.6494 1.46758 12.5625 2.34393C12.6045 2.3836 12.6045 2.3836 12.6473 2.42406C14.3328 4.02269 15.4137 6.25591 16.125 8.43768C16.1831 8.34712 16.2406 8.25623 16.2979 8.16522C16.33 8.11465 16.3621 8.06407 16.3951 8.01196C16.6696 7.5479 16.8121 7.06354 16.945 6.54456C16.9583 6.49446 16.9716 6.44437 16.9853 6.39276C16.9968 6.34768 17.0083 6.30261 17.0202 6.25616C17.0648 6.13478 17.0993 6.07626 17.2032 6.00018C17.3658 5.99393 17.4793 6.00531 17.6093 6.10977C17.8152 6.32036 17.9966 6.54152 18.17 6.77948C18.1982 6.81811 18.2264 6.85674 18.2555 6.89654C19.0017 7.93257 19.6594 9.06913 20.1094 10.2658C20.122 10.2989 20.1346 10.3321 20.1476 10.3662C20.5063 11.3268 20.7038 12.3424 20.8125 13.3596C20.8187 13.4152 20.8249 13.4709 20.8312 13.5282C21.0624 16.0991 20.5143 18.7401 18.8398 20.7557C18.8102 20.79 18.7805 20.8243 18.75 20.8596C18.7175 20.8972 18.685 20.9348 18.6515 20.9736C18.5455 21.0937 18.4375 21.2113 18.3282 21.3283C18.2721 21.389 18.2721 21.389 18.2148 21.451C16.723 23.0083 14.4985 23.9588 12.3494 24.0097C9.77871 24.0377 7.52847 23.1732 5.6701 21.3866C5.34125 21.0636 5.05208 20.7166 4.78129 20.3439C4.76167 20.317 4.74206 20.2901 4.72185 20.2624C3.63436 18.7497 3.07069 16.9561 3.07621 15.0969C3.07627 15.0612 3.07633 15.0255 3.07638 14.9887C3.08271 13.5105 3.5092 12.1898 4.10215 10.8459C4.22458 10.5682 4.34487 10.2896 4.46488 10.0109C4.48859 9.95589 4.51229 9.90085 4.53671 9.84414C4.79072 9.25009 5.01561 8.64784 5.2114 8.0321C5.24882 7.92555 5.28372 7.83033 5.34379 7.73456C5.49613 7.68475 5.49613 7.68475 5.67192 7.68768C6.09037 7.93534 6.2966 8.45585 6.42192 8.90643C6.47743 9.15485 6.52096 9.40536 6.56254 9.65643C7.02175 9.21282 7.46298 8.77571 7.82817 8.25018C7.87874 8.17813 7.87874 8.17813 7.93034 8.10461C8.74457 6.89196 9.32816 5.5319 9.34574 4.0578C9.34641 4.01653 9.34707 3.97525 9.34776 3.93273C9.34182 3.11128 9.1023 2.36417 8.83079 1.59777C8.48917 0.627066 8.48917 0.627066 8.57817 0.187683C8.68649 0.00821657 8.68649 0.00821657 8.82554 -0.0263672Z" fill="#FD7307" />
                              <path d="M11.3317 9.05859C11.7906 9.16449 12.0375 9.6602 12.2704 10.0325C12.2893 10.063 12.3083 10.0936 12.3278 10.125C12.354 10.1667 12.354 10.1667 12.3807 10.2093C12.5081 10.413 12.6297 10.6199 12.7497 10.8281C12.7713 10.8653 12.793 10.9024 12.8153 10.9407C13.5378 12.1886 13.9757 13.4959 14.2497 14.9062C14.8171 14.4354 15.1387 13.9138 15.4463 13.2523C15.47 13.2025 15.4938 13.1528 15.5183 13.1016C15.5391 13.0566 15.5599 13.0117 15.5814 12.9653C15.6673 12.8252 15.7538 12.7469 15.8903 12.6562C16.107 12.6516 16.2203 12.7288 16.3812 12.8676C16.5001 12.9836 16.5888 13.1147 16.6784 13.2539C16.7083 13.2998 16.7083 13.2998 16.7388 13.3467C17.7983 15.0034 18.3715 17.1187 17.9675 19.0781C17.935 19.2199 17.8982 19.3599 17.8591 19.5C17.8437 19.5589 17.8284 19.6178 17.8126 19.6785C17.5835 20.4553 17.1672 21.185 16.6403 21.7969C16.6052 21.8385 16.57 21.8801 16.5338 21.923C15.4907 23.1216 13.9623 23.887 12.3747 24C11.8635 24.0247 11.3724 24.0341 10.8747 23.9062C10.8178 23.894 10.761 23.8818 10.7024 23.8693C9.70346 23.6396 8.79208 23.222 8.01533 22.5469C7.97641 22.5136 7.9375 22.4804 7.89741 22.4462C7.00108 21.6626 6.36038 20.6504 6.04658 19.5C6.03279 19.4516 6.03279 19.4516 6.01872 19.4022C5.63444 18.003 5.7871 16.4517 6.47772 15.1777C6.82843 14.5626 7.27984 14.0418 7.76067 13.5256C7.82735 13.4536 7.8934 13.3811 7.9593 13.3085C8.22323 13.0183 8.49486 12.7384 8.77412 12.4629C9.05768 12.1821 9.31775 11.894 9.5622 11.5781C9.60214 11.5288 9.64218 11.4795 9.68232 11.4304C10.15 10.8535 10.5811 10.2481 10.8941 9.5725C11.105 9.12044 11.105 9.12044 11.3317 9.05859Z" fill="#FC9402" />
                              <path d="M12.3305 17.3404C12.8532 17.7074 13.047 18.5451 13.2429 19.1215C13.4298 19.6694 13.6371 20.1994 13.8883 20.7218C14.192 21.3533 14.3828 21.9088 14.1762 22.6109C13.9645 23.1644 13.5326 23.5575 13.0069 23.81C12.9684 23.8262 12.9298 23.8423 12.8901 23.8589C12.8507 23.8761 12.8113 23.8932 12.7707 23.9109C12.229 24.0506 11.4844 24.0524 10.9821 23.7879C10.9288 23.7534 10.9288 23.7534 10.8745 23.7183C10.8399 23.6963 10.8053 23.6743 10.7697 23.6516C10.6742 23.5848 10.587 23.514 10.4995 23.437C10.4711 23.4131 10.4428 23.3891 10.4136 23.3644C9.92216 22.9157 9.65412 22.1866 9.59841 21.5334C9.53869 20.1544 10.0561 19.013 10.9477 17.979C11.3176 17.5817 11.7546 17.164 12.3305 17.3404Z" fill="#FCE101" />
                              <path d="M13.9219 23.719C13.9219 23.7499 13.9219 23.7809 13.9219 23.8128C13.4055 23.9144 12.9009 23.9797 12.375 24.0003C12.375 23.9848 12.375 23.9693 12.375 23.9534C12.4239 23.9456 12.4239 23.9456 12.4738 23.9377C12.6226 23.9138 12.7712 23.8897 12.9199 23.8655C12.9712 23.8573 13.0225 23.8492 13.0754 23.8408C13.279 23.8075 13.472 23.7752 13.6681 23.7099C13.7812 23.6721 13.7812 23.6721 13.9219 23.719Z" fill="#FE590A" />
                            </g>
                            <defs>
                              <clipPath id="clip0_2974_3708">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          {formatTitle(item?.title ?? "")}
                        </p>
                        <h2 class="ct_fs_24_new mb-0 text-center ct_fw_700 ct_subscription_price">
                          {Number(item?.price ?? 0) === 0 ? (
                            <span className="ct_fw_600 ct_orange_text">FREE</span>
                          ) : (
                            formatPriceLabel(item)
                          )}
                        </h2>
                        <p class="mb-0 mt-4 ct_fw_600 ct_fs_16 ct_blue_text_1 text-center ct_subscription_subtitle">
                          {item?.description ?? ""}
                        </p>
                      </div>
                      <ul class="ct_mt_30 mt-4 ct_subscription_feature_list">
                        {item?.details?.map((detail, index) => (
                          <li
                            key={`${detail?.feature_name || "feature"}-${index}`}
                            className="ct_fs_14"
                          >
                            <i
                              class="fa-solid fa-check ct_subscription_check"
                              style={{
                                backgroundColor:
                                  ["creator", "builder", "leader"].includes(
                                    String(item?.title || "").trim().toLowerCase()
                                  )
                                    ? "#FFB703"
                                    : ["creator pro", "leader prime", "builder elite"].includes(
                                      String(item?.title || "").trim().toLowerCase()
                                    )
                                      ? "#FB8500"
                                      : String(item?.title || "").trim().toLowerCase() === "try me"
                                        ? "#219EBC"
                                        : "#FB8500",
                              }}
                            ></i>{" "}
                            {detail?.feature_name ?? ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div class="mt-auto pt-3 ct_subscription_card_actions">
                      <a
                        class={`${getPlanButtonClass(item)} ct_subscription_btn`}
                        onClick={() => handlePurchasePlan(item)}
                      >
                        Start Now
                      </a>
                      {Number(item?.price ?? 0) !== 0 ? (
                        <span className="ct_subscription_learn" onClick={() => handleRedirectToPlanPage(planType)}>Learn More</span>
                      ) : null}
                      {Number(activeDuration) === 30 && Number(item?.price ?? 0) === 0 ? (
                        <span className="ct_subscription_learn" onClick={() => handleRedirectToPlanPage(planType)}>
                          Upgrade to unlock enhanced visibility
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            {filteredSubscriptions?.length === 0 && !fetchError && (
              <div className="col-12">
                <div className="text-center ct_fs_16 ct_darkblue_text">
                  No subscription plans available for {getDurationLabel(activeDuration)}.
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Subscription;
